import { Link } from "react-router-dom";
import { Phone, CalendarCheck, MapPin } from "lucide-react";
import { SITE } from "@/config/site";
import { telLink } from "@/utils/whatsapp";

export const Hero = () => (
  <section className="relative overflow-hidden bg-white" data-testid="hero-section">
    <div className="pointer-events-none absolute inset-0" aria-hidden="true">
      <div className="absolute -left-32 top-10 h-80 w-80 rounded-full bg-navy-50" />
      <div className="absolute -bottom-40 right-0 h-96 w-96 rounded-full bg-emerald-50" />
    </div>
    <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-4 py-16 sm:px-6 sm:py-20 lg:grid-cols-2 lg:gap-16 lg:px-8 lg:py-24">
      <div className="animate-fade-up">
        <p className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-4 py-1.5 text-xs font-semibold text-emerald-700" data-testid="hero-badge">
          <MapPin className="h-3.5 w-3.5" aria-hidden="true" /> Serving Kasdol & nearby communities
        </p>
        <h1 className="mt-6 font-heading text-4xl font-extrabold leading-[1.15] tracking-tight text-navy-900 sm:text-5xl lg:text-6xl">
          Trusted Healthcare for You and Your <span className="text-emerald-600">Family</span>
        </h1>
        <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg">
          Quality medical care, experienced doctors and compassionate support — closer to home in Kasdol.
        </p>
        <div className="mt-8 flex flex-col gap-3 sm:flex-row">
          <Link
            to="/appointments"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-navy-800 px-7 py-3.5 text-sm font-semibold text-white transition-all duration-200 hover:bg-navy-700 hover:shadow-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy-600 focus-visible:ring-offset-2"
            data-testid="hero-book-appointment-button"
          >
            <CalendarCheck className="h-4 w-4" aria-hidden="true" /> Book an Appointment
          </Link>
          <a
            href={telLink}
            className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-navy-200 px-7 py-3.5 text-sm font-semibold text-navy-800 transition-all duration-200 hover:border-navy-800 hover:bg-navy-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy-600 focus-visible:ring-offset-2"
            data-testid="hero-call-button"
          >
            <Phone className="h-4 w-4" aria-hidden="true" /> Call {SITE.phoneDisplay}
          </a>
        </div>
        <p className="mt-6 text-xs text-slate-500">{SITE.tagline}</p>
      </div>

      <div className="relative animate-fade-up" style={{ animationDelay: "150ms" }}>
        <div className="overflow-hidden rounded-3xl shadow-2xl">
          <img
            src="/images/hospital-hero.jpg"
            alt={`${SITE.name} — modern hospital building in Kasdol`}
            className="h-72 w-full object-cover sm:h-96 lg:h-[480px]"
            fetchPriority="high"
            data-testid="hero-image"
          />
        </div>
        <div className="absolute -bottom-6 left-4 flex items-center gap-3 rounded-2xl bg-white p-4 shadow-xl sm:left-8" data-testid="hero-card-appointments">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-100 text-emerald-600">
            <CalendarCheck className="h-5 w-5" aria-hidden="true" />
          </span>
          <div>
            <p className="text-sm font-semibold text-navy-900">Easy Appointments</p>
            <p className="text-xs text-slate-500">Book on WhatsApp in a minute</p>
          </div>
        </div>
        <div className="absolute -top-5 right-4 rounded-2xl bg-navy-900 px-5 py-3 text-white shadow-xl sm:right-8" data-testid="hero-card-care">
          <p className="text-sm font-semibold">Patient-First Care</p>
          <p className="text-xs text-slate-300">Compassion in every consultation</p>
        </div>
      </div>
    </div>
  </section>
);
